/**
 * Envelope tile cache level configuration.
 *
 * Level spacings must satisfy the integer multiple constraint:
 * each level's spacing must be an exact integer multiple of the next finer level.
 *
 * Starting configuration: L0 targets ~6 months, K = 100.
 * See docs/seismic/envelope-tile-cache-design.md for rationale.
 */
/** Layout version -- update this when other constants change */
export const ENVELOPE_LAYOUT_VERSION = 2;
/** ~6 months */
export const L0_SPACING = 15750;
/** Scale factor between adjacent levels. */
export const K_FACTOR = 100;
/** Point spacing in seconds for each level, coarsest (L0) to finest (L2). */
export const LEVEL_SPACINGS = [L0_SPACING, L0_SPACING / K_FACTOR, L0_SPACING / (Math.pow(K_FACTOR, 2))];
/** Number of envelope points per tile for L0 and L1. */
export const BASE_POINTS_PER_TILE = 1000;
/** L2 uses more points per tile to reduce total tile count. */
export const L2_TILE_MULTIPLIER = 20;
/**
 * Number of envelope points per tile, indexed by level.
 * L0/L1: 1000 points, L2: 20000 points.
 */
export const POINTS_PER_TILE = [
    BASE_POINTS_PER_TILE, BASE_POINTS_PER_TILE, BASE_POINTS_PER_TILE * L2_TILE_MULTIPLIER
];
/** Number of stored envelope levels. */
export const NUM_LEVELS = LEVEL_SPACINGS.length;
/** The finest (highest-resolution) stored envelope level. */
export const FINEST_LEVEL = NUM_LEVELS - 1;
/**
 * Fixed amplitude range per instrument type (second char of SEED channel code).
 * Physical units: H/L = m/s (velocity), N = m/s² (acceleration).
 */
export const AMPLITUDE_RANGES = {
    H: 0.05, // High-gain seismometer, ±0.05 m/s
    L: 0.05, // Low-gain seismometer, ±0.05 m/s
    N: 40, // Accelerometer, ±40 m/s²
};
/** Sentinel value for "no data" in Int16 envelope arrays. */
export const NO_DATA_SENTINEL = -32768;
function getUrlParam(name) {
    if (typeof window === "undefined")
        return null;
    return new URLSearchParams(window.location.search).get(name);
}
/** S3 bucket where envelope tiles are stored. */
export const S3_BUCKET = "models-resources";
export const S3_STAGING_BUCKET = "models-resources-qa";
/** Token-service environment for envelope uploads; ?tokenServiceEnv=staging for testing. */
export function getTokenServiceEnv() {
    return getUrlParam("tokenServiceEnv") === "staging" ? "staging" : "production";
}
function getS3Bucket() {
    return getTokenServiceEnv() === "staging" ? S3_STAGING_BUCKET : S3_BUCKET;
}
/** AWS region of the envelope tile bucket. */
export const AWS_REGION = "us-east-1";
/** S3 key prefix for envelope tiles. */
export const S3_PREFIX = "collaborative-learning/envelopes/";
/** Base URL of the S3 bucket hosting envelope tiles. */
export function getTileBaseUrl() {
    return `https://${getS3Bucket()}.s3.amazonaws.com/`;
}
/** Duration of each raw data fetch chunk in seconds. */
export const RAW_CHUNK_DURATION = 7200; // 2 hours
//# sourceMappingURL=data:application/json;base64,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